import { useMemo, type ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/cn';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  /** Optional CTA — render any clickable element (Button, Link, etc.). */
  action?: ReactNode;
  /**
   * Tone tweaks the dotted-grid aura. `brand` is for hero-ish empties on the
   * Overview / Welcome card, `quiet` is the default for in-card empties.
   */
  tone?: 'quiet' | 'brand';
  /** Show a quirky rotating tip under the description (subtle personality). */
  withTip?: boolean;
  className?: string;
}

const TIPS: string[] = [
  'Tip: ⌘K opens the command palette from anywhere.',
  'Tip: save your current filters as a view from the Topbar — name it whatever you want.',
  'Tip: your URL is shareable — drop it in Slack to send a teammate the same view.',
  'Tip: switch the compare period (off · prev · year-on-year) right next to the date picker.',
  'Tip: pin your favorite stores so they land at the top of the picker.',
  'Tip: numbers animate when they change — easier to spot a sudden swing.',
];

/**
 * Empty state — used everywhere a list/chart has no data. v2 adds:
 *   - subtle brand-tinted dotted-grid background (per Emil: very low opacity)
 *   - optional CTA slot (most useful when the user can do something to fix it)
 *   - rotating tip line so the empty space teaches the product over time
 *
 * The component is intentionally compact — long copy belongs in the page,
 * not here. Stays readable in both light and dark themes.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  tone = 'quiet',
  withTip,
  className,
}: EmptyStateProps) {
  const tip = useMemo(() => {
    if (!withTip) return null;
    return TIPS[Math.floor(Math.random() * TIPS.length)];
  }, [withTip]);

  return (
    <div
      className={cn(
        'relative isolate flex flex-col items-center justify-center gap-3 overflow-hidden rounded-lg border border-dashed border-border bg-surface px-6 py-12 text-center',
        tone === 'brand' ? 'bg-dotgrid-brand' : 'bg-dotgrid',
        className,
      )}
    >
      {icon ? (
        <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-surface-elevated text-muted-foreground shadow-soft ring-1 ring-inset ring-border">
          {icon}
        </div>
      ) : null}
      <div className="space-y-1">
        <p className="font-display text-sm font-semibold tracking-tight">{title}</p>
        {description ? (
          <p className="mx-auto max-w-sm text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? <div className="pt-1">{action}</div> : null}
      {tip ? (
        <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground/80">
          <Sparkles className="h-3 w-3 opacity-70" aria-hidden />
          <span>{tip}</span>
        </p>
      ) : null}
    </div>
  );
}
